// Color Selection page — homeowner-facing /colors route.
//
// Shipped (Apr 30, 2026): drip edge picker added, finger signature pad added,
// generates a signed Color Confirmation Addendum PDF (lib/colorAddendumPDF),
// uploads to deal-documents/color-addendums/{deal_id}/{ts}-signed.pdf,
// flips color_addendum_signed=true + color_addendum_signed_date + url, and
// tells the assigned RM through ho-portal-notify (Team line + in-portal; since
// Oct 21, 2026 — it used to text the RM from the customer line).
//
// Lock: once material_order_date is set, the page becomes read-only — any
// changes after material order go through Section 7 change-order workflow.

import { useEffect, useState } from "react";
import { useHomeownerAuth } from "./useHomeownerAuth";
import HomeownerGate from "./HomeownerGate";
import SignatureCanvas from "./SignatureCanvas";
import { supabaseAnon as supabase } from "../../lib/supabase";
import { authTriple, hoPortalNotify, hoRecordColorAddendum } from "../../lib/homeownerRpc";
import { DRIP_EDGE_COLORS, matchDripEdgeColor } from "../../lib/colorOptions";

/* Owens Corning TruDefinition Duration Premium ("Max Def") colors for
   visual swatches. Max Def Black Sable added per Greg (Oct 28, 2026). */
const POPULAR_SHINGLE_COLORS = [
  { name: "Max Def Onyx Black", hex: "#1c1c1c" },
  { name: "Max Def Twilight Black", hex: "#2a2628" },
  { name: "Max Def Quarry Gray", hex: "#8a8a8a" },
  { name: "Max Def Storm Cloud", hex: "#5a5d63" },
  { name: "Max Def Heather Blend", hex: "#6e6066" },
  { name: "Max Def Weathered Wood", hex: "#6b6356" },
  { name: "Max Def Driftwood", hex: "#8b7355" },
  { name: "Max Def Brownwood", hex: "#5c4033" },
  { name: "Max Def Sand Dune", hex: "#b8a17e" },
  { name: "Max Def Frosted Oak", hex: "#b09e84" },
  { name: "Max Def Black Sable", hex: "#1c1c1c" },
];

/* Drip edge: White / Black / Brown only (Oct 22, 2026, per Greg). The list
   lives in src/lib/colorOptions.ts with the signing page and the deal page. */

export default function ColorSelection() {
  const auth = useHomeownerAuth();
  const { deal } = auth;

  const [shingle, setShingle] = useState<string | null>(deal?.shingle_color || null);
  const [drip, setDrip] = useState<string | null>(matchDripEdgeColor(deal?.drip_edge_color));
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Re-prime selections when the deal arrives from the auth hook
  useEffect(() => {
    if (deal) {
      setShingle(deal.shingle_color || null);
      // An older hand-typed value that isn't one of the three options stays
      // unselected, so the homeowner picks from the standard list.
      setDrip(matchDripEdgeColor(deal.drip_edge_color));
    }
  }, [deal]);

  // Lock the page once material has been ordered. /colors becomes
  // read-only — Section 7 change-order workflow takes over from here.
  const lockedForMaterialOrder = !!deal?.material_order_date;

  // Already-signed state: deal has a signed addendum URL on file.
  const alreadySigned = !!deal?.color_addendum_signed && !!deal?.color_addendum_url;

  const canSubmit = !lockedForMaterialOrder && !!shingle && !!drip && !!signatureData && !saving;

  const handleSubmit = async () => {
    if (!canSubmit || !deal) return;
    setSaving(true);
    setSaveError(null);

    try {
      // 1. Upload signature image to deal-documents (canonical bucket).
      //    Same Apr 30 fix as SigningPage — earlier code wrote to bucket
      //    "documents" which doesn't exist. Now uses deal-documents with
      //    signed URLs that work for both anon (HO) and authenticated (staff).
      let sigUrl: string | null = null;
      let sigPath: string | null = null;
      if (signatureData) {
        const sigBlob = await (await fetch(signatureData)).blob();
        sigPath = `signatures/${deal.id}/${Date.now()}-color-addendum.png`;
        const { error: sigErr } = await supabase.storage
          .from("deal-documents")
          .upload(sigPath, sigBlob, { contentType: "image/png", upsert: true });
        if (sigErr) throw new Error(`Signature upload failed: ${sigErr.message}`);
        const { data: signed } = await supabase.storage
          .from("deal-documents")
          .createSignedUrl(sigPath, 31536000);
        sigUrl = signed?.signedUrl ?? null;
      }

      // 2 + 3. Build the full deal in memory (deal is the full row returned by
      //   ho_resolve_deal) with the chosen colors overlaid, so the PDF reflects
      //   them. No direct deals read/write here — the DB side is recorded by
      //   ho_record_color_addendum below.
      const fullDeal = { ...deal, shingle_color: shingle, drip_edge_color: drip };

      const claim = fullDeal.claim_number || "RETAIL";
      const lastName = (fullDeal.homeowner_name || "").split(" ").pop()?.toUpperCase() || "";
      const addendumNumber = `CCA-${claim}-${lastName}`;

      const { generateColorAddendumPDF } = await import("../../lib/colorAddendumPDF");
      const pdfBlob = await generateColorAddendumPDF(
        fullDeal as never,
        signatureData,
        addendumNumber,
        fullDeal.install_agreement_signed_date ?? null,
      );

      // 4. Upload addendum PDF (canonical bucket: deal-documents).
      const pdfPath = `color-addendums/${deal.id}/${Date.now()}-signed.pdf`;
      const { error: pdfErr } = await supabase.storage
        .from("deal-documents")
        .upload(pdfPath, pdfBlob, {
          contentType: "application/pdf",
          upsert: false,
        });
      if (pdfErr) throw pdfErr;

      // Signed URL (1yr) — bucket is private; signed URL works for both anon and auth
      const { data: pdfSigned, error: pdfSignErr } = await supabase.storage
        .from("deal-documents")
        .createSignedUrl(pdfPath, 31536000);
      if (pdfSignErr || !pdfSigned?.signedUrl) {
        throw new Error(`createSignedUrl failed: ${pdfSignErr?.message ?? "no url"}`);
      }
      const pdfUrl = pdfSigned.signedUrl;

      // 5-7. Record the addendum server-side (colors + gate flags +
      //   deal_documents + activity_log) via a token-validated RPC.
      const { error: recErr } = await hoRecordColorAddendum(authTriple(deal), {
        shingle: shingle as string,
        drip: drip as string,
        addendumUrl: pdfUrl,
        pdfPath,
        addendumNumber,
        signatureUrl: sigUrl,
        signaturePath: sigPath,
      });
      if (recErr) throw recErr;

      // 8. Tell the RM and split RM (Team line + in-portal). Best-effort: the
      //    colors are already saved, so a failed alert doesn't fail the page.
      const notify = await hoPortalNotify(authTriple(deal), "colors_locked");
      if (!notify.ok) console.warn("[colors] RM alert failed:", notify.code);

      setSaved(true);
    } catch (err) {
      console.error("Color addendum signing failed:", err);
      setSaveError("Something went wrong saving your colors. Please try again or call us at (470) 823-8816.");
    } finally {
      setSaving(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────

  return (
    <HomeownerGate {...auth} title="Confirm Your Colors">
      {deal && (
        <div className="space-y-4">
          {/* SUCCESS */}
          {saved && (
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="text-4xl mb-3">🎨</div>
              <h1 className="text-xl font-bold text-bardin-ink mb-2">Colors Confirmed!</h1>
              <p className="text-gray-600 mb-1">
                Shingle: <span className="font-semibold">{shingle}</span>
              </p>
              <p className="text-gray-600 mb-3">
                Drip edge: <span className="font-semibold">{drip}</span>
              </p>
              <p className="text-sm text-gray-400">
                Your Restore Manager has been notified. A signed Color Confirmation Addendum has been added to your project documents.
              </p>
            </div>
          )}

          {/* ALREADY SIGNED — show summary + download */}
          {!saved && alreadySigned && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="text-3xl">✅</div>
                <h1 className="text-lg font-bold text-bardin-ink">Colors Already Confirmed</h1>
              </div>
              <p className="text-sm text-gray-600">
                Shingle: <span className="font-semibold">{deal.shingle_color}</span>
              </p>
              <p className="text-sm text-gray-600 mb-3">
                Drip edge: <span className="font-semibold">{deal.drip_edge_color}</span>
              </p>
              {deal.color_addendum_url && (
                <a
                  href={deal.color_addendum_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-bardin-teal text-white text-sm font-medium rounded-lg px-4 py-2 hover:bg-bardin-teal/90 transition"
                >
                  View signed addendum
                </a>
              )}
              {!lockedForMaterialOrder && (
                <p className="mt-4 text-xs text-slate-500">
                  Need to change a color? Contact your Restore Manager. Once materials are ordered, color changes follow the change-order process.
                </p>
              )}
            </div>
          )}

          {/* MATERIAL ORDERED — read-only lock */}
          {!saved && !alreadySigned && lockedForMaterialOrder && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="text-2xl">🔒</div>
                <h1 className="text-base font-bold text-amber-900">Materials have been ordered</h1>
              </div>
              <p className="text-sm text-amber-900">
                Color changes after material order go through the change-order process. Please contact your Restore Manager to discuss any changes.
              </p>
            </div>
          )}

          {/* PICKER */}
          {!saved && !alreadySigned && !lockedForMaterialOrder && (
            <>
              {/* Shingle */}
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <h1 className="text-lg font-bold text-bardin-ink mb-1">Pick Your Shingle Color</h1>
                <p className="text-sm text-gray-500 mb-4">{deal.property_street}</p>

                <a
                  href="https://www.owenscorning.com/en-us/roofing/designeyeq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block border-2 border-bardin-teal rounded-xl p-4 mb-4 hover:bg-bardin-sky/30 transition"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🏠</span>
                    <div>
                      <p className="font-semibold text-bardin-teal text-sm">See colors on YOUR house</p>
                      <p className="text-xs text-gray-500">Open the Owens Corning Design EyeQ visualizer</p>
                    </div>
                    <span className="ml-auto text-bardin-teal">→</span>
                  </div>
                </a>

                <h2 className="text-sm font-semibold text-bardin-ink mb-3">Popular Colors</h2>
                <div className="grid grid-cols-3 gap-2">
                  {POPULAR_SHINGLE_COLORS.map((c) => (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => setShingle(c.name)}
                      className={`rounded-lg p-2 border-2 transition text-center ${
                        shingle === c.name
                          ? "border-bardin-orange ring-2 ring-bardin-orange/30"
                          : "border-gray-200 hover:border-bardin-teal"
                      }`}
                    >
                      <div className="w-full h-8 rounded-md mb-1" style={{ backgroundColor: c.hex }} />
                      <span className="text-[10px] leading-tight text-bardin-ink">{c.name}</span>
                    </button>
                  ))}
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-bardin-ink mb-1">Or type a color name</label>
                  <input
                    type="text"
                    value={shingle || ""}
                    onChange={(e) => setShingle(e.target.value)}
                    placeholder="e.g. Onyx Black"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                  />
                </div>
              </div>

              {/* Drip edge */}
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <h1 className="text-lg font-bold text-bardin-ink mb-3">Pick Your Drip Edge Color</h1>
                <p className="text-sm text-gray-500 mb-4">
                  Drip edge is the metal trim along the edge of your roof. It comes in white, black or brown — most homeowners match their gutters or shingle.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {DRIP_EDGE_COLORS.map((c) => (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => setDrip(c.name)}
                      className={`rounded-lg p-2 border-2 transition text-center ${
                        drip === c.name
                          ? "border-bardin-orange ring-2 ring-bardin-orange/30"
                          : "border-gray-200 hover:border-bardin-teal"
                      }`}
                    >
                      <div
                        className="w-full h-8 rounded-md mb-1 border border-slate-200"
                        style={{ backgroundColor: c.hex }}
                      />
                      <span className="text-[10px] leading-tight text-bardin-ink">{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Signature + summary + submit */}
              {(shingle || drip) && (
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <h2 className="text-lg font-bold text-bardin-ink mb-2">Confirm & Sign</h2>
                  <p className="text-sm text-gray-500 mb-3">
                    By signing, you're agreeing to these color selections as a written change order under Section 7 of your Install Agreement. We'll generate a one-page Color Confirmation Addendum and add it to your project documents.
                  </p>

                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Shingle:</span>
                      <span className="font-semibold text-bardin-ink">{shingle || "(none yet)"}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-slate-500">Drip edge:</span>
                      <span className="font-semibold text-bardin-ink">{drip || "(none yet)"}</span>
                    </div>
                  </div>

                  <label className="block text-sm font-medium text-bardin-ink mb-2">Your signature</label>
                  <SignatureCanvas
                    onSign={(dataUrl) => setSignatureData(dataUrl)}
                    onClear={() => setSignatureData(null)}
                    defaultTypedName={deal?.homeowner_name || ""}
                  />

                  {saveError && (
                    <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                      {saveError}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className="mt-4 w-full bg-bardin-orange text-white font-semibold rounded-lg py-3 hover:bg-bardin-orange/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Saving..." : !shingle || !drip ? "Pick both colors to continue" : !signatureData ? "Sign above to continue" : "Confirm & Sign Addendum"}
                  </button>

                  <div className="mt-3 text-center text-xs text-slate-400">
                    Not sure? Ask your Restore Manager for samples.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </HomeownerGate>
  );
}
